import { insuranceInput } from '@/api/api';
import { cloneDeep, get, isNil } from 'lodash';
import dayjs from 'dayjs';
import { filterClauses80291 } from '@/mixin/special/special-80291';

export const EnumClauseTypes = {
  DYNAMIC: 1,
  STATIC: 2,
  WHOLE: 3,
  OTHER: 4
}

/**
 * 处理责任数据
 * @param allClauseList 接口返回的所有责任列表
 * @param type 0-订单维度责任 1-计划维度责任
 */
export function processClauses(allClauseList, type) {
  return allClauseList
    .filter((clause) => clause.type === type)
    .map((clause) => {
      if (clause && !clause.selected && get(clause, 'optionList.length')) {
        if (clause.selected) {
          // 可展示列表（disabled=false）优先取默认值（isDefault=1），没有则取第一个
          let ableOptionList = clause.optionList.filter((o) => !o.disabled);
          if (ableOptionList.length > 0) {
            let defaultOptionList = ableOptionList.filter((o) => o.isDefault === 1);
            if (defaultOptionList.length > 0) {
              clause.selected = defaultOptionList[0];
            } else {
              clause.selected = ableOptionList[0];
            }
          }
          // console.log('clause.selected', clause.selected);
        }
      } else {
        // console.log('现在是服务端返回更新数据');
      }
      return clause;
    })
    .sort((a, b) => a.sort - b.sort);
}

/**
 * 设置保障责任配置错误
 * @param allClauseList
 */
export function setClausesErrMsg(allClauseList, toast) {
  // console.log('💠 设置保障责任配置错误', allClauseList);
  for (let i = 0; i < allClauseList.length; i++) {
    const clause = allClauseList[i];
    if (clause.errorMessages) {
      toast && toast(clause.errorMessages[0]);
      return;
    }
  }
}

// 算费之后更新每个职业中的 perPrice 和 premium
export const updateProfessionsByJobs = (professions, jobs) => {
  if (!jobs) {
    return professions
  }
  const cloneProfessions = cloneDeep(professions)
  cloneProfessions.forEach(profession => {
    const matchedJob = jobs.find(j => j.jobCode + '' == profession.code + '')
    if (matchedJob) {
      // 职业均价，因为有些产品会有职业加费，price 是职业细类价格，perPrice计划下人员均价
      profession.perPrice = matchedJob.price || matchedJob.perPrice
      profession.premium = matchedJob.premium
    }
  })
  return cloneProfessions
};

// 算费之后更新每个计划中的PlanClauseList，主要是塞入premiumMap
export const updatePlanClauseList = (oldPlanClauseList, newClauses) => {
  for(let i = 0; i < oldPlanClauseList.length; i++) {
    const clause = oldPlanClauseList[i]
    const matchedClause = newClauses.find(c => c.clauseCode + '' == clause.clauseCode + '')
    if (matchedClause) {
      clause.premiumMap = matchedClause.premiumMap
    }
  }
  return oldPlanClauseList
};

// 报价页：算费之后更新每个职业中的 perPrice 和 premium
export const updateProfessionsByJobsQuote = (professionList, jobs) => {
  if (!jobs) {
    return professionList
  }
  const cloneProfessionList = cloneDeep(professionList)
  cloneProfessionList.forEach(profession => {
    // 职业细类
    const professionJobCode = get(profession, 'professions.code')
    // 职业大类
    const professionJobType = get(profession, 'professions.val')
    let matchedJob
    // 因为报价页有时候只需要选择职业大类，所以没有 jobCode，所以只要匹配职业大类相同
    if (professionJobCode) {
      matchedJob = jobs.find(j => j.jobCode + '' == professionJobCode + '')
    } else if (professionJobType) {
      matchedJob = jobs.find(j => j.jobType + '' == professionJobType + '')
    }
    if (matchedJob) {
      // 职业均价，因为有些产品会有职业加费，price 是职业细类价格，perPrice计划下人员均价
      profession.perPrice = matchedJob.price || matchedJob.perPrice
      profession.premium = matchedJob.premium
    }
  })
  return cloneProfessionList
};

/**
 * 校验职业和当前保额是否匹配
 * 例如意外身故选择了 80 万，5类职业费率表中没有这个责任或者没有 80 万的选项，则报错
 * https://devops.aliyun.com/projex/req/JYYF-10303# 《【团险】职业选择不符合投保条件的提醒优化》
 * @param {*} profession - 选择的职业,
 * @param {*} scheme - 当前计划,
 * @param {*} duration - 当前投保期间 如 1Y,
 * @param {*} clausePremium - 费率表,
 * @returns
 */
export const getProfessionErrorByPremiumTable = async ({
  profession,
  scheme,
  duration,
  clausePremium,
}) => {
  try {
    const { deductible, deductibleRate, planList: clauseList } = scheme;
    // console.log("🚀 ~ getProfessionErrorByPremiumTable ~ 入参")

    let durationKey = 'D_' + duration.duration + duration.durationUnit;
    // 费率表对应的 key
    let currentPremiumKey = durationKey + '_P_' + profession.val;
    // 当前职业对应的费率表数组
    let currentPremium = cloneDeep(clausePremium[currentPremiumKey]);
    let db_key = !isNil(deductible) ? '_DB_' + deductible : '';
    let dr_key = !isNil(deductibleRate) ? '_DR_' + deductibleRate : '';
    // 免赔额和免赔率，如果有，则拼接上，总数和保障责任对应上
    if (db_key || dr_key) {
      currentPremium = currentPremium.concat(clausePremium[currentPremiumKey + db_key + dr_key]);
    }
    // console.log("💰 ~ 当前投保期间和职业类别对应的费率表数组:", currentPremium)
    for (let clause of clauseList) {
      if (clause.merge || get(clause, 'selected.nameStr') === '同主险') {
        // 同主险的责任不校验，因为他关联的那个主险肯定是一样的校验，同主险的报错信息没有意义
        continue;
      }
      let sameClause = currentPremium.find((item) => {
        return item.clauseCode === clause.clauseCode;
      });
      if (!sameClause) {
        console.log(
          `费率表不存在该责任【${clause.clauseName}】 key 为${currentPremiumKey + (db_key || '') + (dr_key || '')}`
        );
        return `该职业不支持投保${clause.selected.nameStr}${clause.clauseName}`;
      }
      const sameInsured = sameClause.groupProductPremiumList.find((item) => {
        return item.insured === clause.selected.insured;
      });
      if (!sameInsured) {
        console.log(
          `费率表不存在该责任【${clause.clauseName}】的选项 key 为${
            currentPremiumKey + (db_key || '') + (dr_key || '')
          }`
        );
        return `该职业不支持投保${clause.selected.nameStr}${clause.clauseName}`;
      }
    }
    return '';
  } catch (error) {
    console.error('获取职业和保额约束错误，跳过校验', error);
    return '';
  }
};

/**
 * 校验职业和当前保额是否匹配 - 适用于新配置产品，新配置由服务端接口来判断
 * 例如意外身故选择了 80 万，5类职业费率表中没有这个责任或者没有 80 万的选项，则报错
 * https://devops.aliyun.com/projex/req/JYYF-10303# 《【团险】职业选择不符合投保条件的提醒优化》
 * @param {*} profession - 选择的职业,
 * @param {*} schemeIndex - 当前计划,
 * @returns
 */
export const getProfessionErrorByInput = async ({
  profession,
  getInsuranceInputData,
  schemeIndex,
  professionIndex
}) => {
  // console.log('💰 ~ getProfessionErrorByInput:', profession, getInsuranceInputData, schemeIndex, professionIndex);
  try {
    const newGetInsuranceInputData = cloneDeep(getInsuranceInputData)
    const schemeList = get(newGetInsuranceInputData, 'schemeList', [])
    const scheme = schemeList[schemeIndex]
    if (scheme.jobs[professionIndex]) {
      scheme.jobs[professionIndex].des = profession.des
      scheme.jobs[professionIndex].jobCode = profession.code
      scheme.jobs[professionIndex].jobType = profession.val
    } else {
      scheme.jobs.push({
        des: profession.des,
        jobCode: profession.code,
        jobType: profession.val,
        peopleNum: 0
      })
    }
    // console.log('💰 ~ newGetInsuranceInputData:', newGetInsuranceInputData);
    const res = await insuranceInput(newGetInsuranceInputData)
    // console.log('💰 ~ res:', res);
    const currentScheme = get(getInsuranceInputData, `schemeList.${schemeIndex}`, {})
    const newScheme = get(res, `schemeList.${schemeIndex}`, {})
    for (let clause of currentScheme.clauses) {
      if (clause.clauseType === EnumClauseTypes.STATIC) {
        // 静态责任不需要判断保额约束
        continue
      }
      const findClause = newScheme.clauses.find((c) => c.clauseCode === clause.clauseCode)
      if (findClause) {
        // 当前责任选中值
        const currentSelectedValue = get(clause, 'selected.value')
        // 如果在接口返回的对应选项是 disabled，那么就是不支持这个责任投保这个保额
        const matchedOption = findClause.optionList.find((o) => o.value === currentSelectedValue)
        if (!matchedOption || matchedOption.disabled) {
          return `该职业不支持投保${clause.selected.showValue}${clause.clauseName}`
        }
      }
    }
    return ''
  } catch (error) {
    console.error('获取职业和保额约束错误，跳过校验', error);
    return '';
  }
  return ''
}

/**
 * 前端处理某些特殊产品的责任
 * @param {*} clauses 
 * @returns 
 */
export function transformClausesSpecial(productId) {
  const funcMap = {
    '80291': filterClauses80291,
  }
  if (funcMap[productId]) {
    return funcMap[productId];
  }
  return null;
}

/**
 * 前端处理某些特殊产品的责任
 * 取接口返回的某个计划下的责任，因为有某些产品的责任有同主险的选项
 * 比如【80271美亚】扩展5米以上高处作业，只有两个选项“扩展/不扩展”，扩展就是保额同主险，不扩展就是保额为 0 的那个选项
 * @param clauses
 */
export function filterClausesForFront(clauses) {
  let cloneClauses = cloneDeep(clauses);
  const productId = get(clauses, '0.productId', '');
  try {
    cloneClauses.forEach((clause) => {
      if (clause.isAmountSameAnotherClause === 1 && clause.anotherClauseCode) {
        const anotherClause = cloneClauses.find((c) => c.clauseCode === clause.anotherClauseCode);
        if (anotherClause) {
          clause.optionList = clause.optionList.filter((option) => {
            return option.value == 0 || option.value == anotherClause.selected.value;
          });
          // 接口返回的选中项
          const originalSelected = cloneDeep(clause.selected);
          clause.selected =
            originalSelected.value == 0
              ? originalSelected
              : clause.optionList.find((option) => option.value == anotherClause.selected.value);
        }
      }
    });
    const transformClausesSpecialFunc = transformClausesSpecial(productId);
    if (transformClausesSpecialFunc) {
      cloneClauses = transformClausesSpecialFunc(cloneClauses);
    }
  } catch (err) {
    console.error(err);
    return cloneClauses;
  }
  return cloneClauses;
}



// WEBPACK FOOTER //
// ./src/util/clause.js